import { useNavigate } from "@tanstack/react-router";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabaseClient } from "../utils/supabase";
import { toast } from "@patient-watch/ui/sonner";
import { useCopy } from "../i18n/locale";

interface UseFormSubmissionProps {
    entry_id: string | undefined;
    profile_id: string | undefined;
    nextRoute?: string;
    onSuccess?: () => void;
}

interface SubmitFormData {
    data: Record<string, any>;
    customNextRoute?: string;
}

export function useFormSubmission({ entry_id, profile_id, nextRoute, onSuccess }: UseFormSubmissionProps) {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const copy = useCopy();

    const mutation = useMutation({
        mutationFn: async ({ data }: SubmitFormData) => {
            if (!entry_id) {
                throw new Error("No entry ID provided");
            }

            const { error } = await supabaseClient.from("entries").update(data).eq("entry_id", entry_id);

            if (error) {
                throw error;
            }

            return data;
        },
        onSuccess: async ({ customNextRoute }) => {
            // Invalidate the entry query cache to refresh the data
            await queryClient.invalidateQueries({
                queryKey: ["entry", entry_id],
            });

            // Call custom success handler if provided
            if (onSuccess) {
                onSuccess();
            }

            // Navigate to next route
            const targetRoute = customNextRoute || nextRoute;
            if (targetRoute && entry_id && profile_id) {
                navigate({
                    to: targetRoute,
                    search: { entry_id, profile_id } as any,
                });
            }
        },
        onError: (error) => {
            console.error("Form submission error:", error);
            toast.error(copy.formShell.saveFailed);
        },
    });

    const submitForm = (data: Record<string, any>, customNextRoute?: string) => {
        mutation.mutate({ data, customNextRoute });
    };

    return {
        submitting: mutation.isPending,
        submitForm,
        error: mutation.error,
        isSuccess: mutation.isSuccess,
    };
}
